import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { TimeManagerTeam } from '../entities/TimeManagerTeam';
import { Subject } from 'rxjs';
import { InFilter } from '../baseServices/filter.interface';
import { GlobalConstants } from '../constant/constant';
import { StoreName } from '../entities/StoreName';
import { DataChangeEmitPayload, DataChangeEmitWho, DataChangeEmitWhat } from '../models/data-change-emit/data-change-emit-payload';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { EntityHelper, Check } from '../utils/entity-helper';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';

@Injectable({
  providedIn: 'root',
})
export class TimeManagerTeamService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<TimeManagerTeam>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(timeManagerTeam: TimeManagerTeam, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: timeManagerTeam, who: who, what: what });
  }

  getEmitChangeCallback(timeManagerTeam: TimeManagerTeam, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForTimeManagerTeam + '_' + timeManagerTeam.RecordId,
      fn: this.emitChange.bind(this, timeManagerTeam, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.TimeManager2Team], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getTimeManagerTeamByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerTeam> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<TimeManagerTeam>(StoreName.TimeManager2Team, onlineId, TimeManagerTeam, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as TimeManagerTeam;

    if (this.checkIsTimeManagerTeamSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getTimeManagerTeamByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerTeam> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<TimeManagerTeam>(StoreName.TimeManager2Team, recordId, TimeManagerTeam, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as TimeManagerTeam;

    if (this.checkIsTimeManagerTeamSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getTimeManagerTeamsByUserId(userId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerTeam[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<TimeManagerTeam>(StoreName.TimeManager2Team, GlobalConstants.KEY_NAMES.UserId, userId, TimeManagerTeam, transaction);

    let records = result as TimeManagerTeam[];

    if (!includeDeleted) {
      records = records.filter(timeManagerTeam => !this.checkIsTimeManagerTeamSoftDeleted(timeManagerTeam));
    }

    return records;
  }

  async getTimeManagerTeamsByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerTeam[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filter: InFilter = { in: { field: 'Id', searchArray: onlineIds } };

    const result = await this.repository.getAll<TimeManagerTeam>(StoreName.TimeManager2Team, filter, TimeManagerTeam, transaction);

    let records = result as TimeManagerTeam[];

    if (!includeDeleted) {
      records = result.filter(timeManagerTeam => !this.checkIsTimeManagerTeamSoftDeleted(timeManagerTeam));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createTimeManagerTeam(who: DataChangeEmitWho, recordToCreate: TimeManagerTeam, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateTimeManagerTeamCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.TimeManager2Team, transaction);

    // LC5. Get created record
    const createdRecord = await this.getTimeManagerTeamByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateTimeManagerTeam(
    who: DataChangeEmitWho,
    recordToUpdate: TimeManagerTeam,
    skipLocalCopyValidation: boolean,
    localCopy: TimeManagerTeam | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getTimeManagerTeamByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateTimeManagerTeamUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeManagerTeam>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<TimeManagerTeam>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateTimeManagerTeam(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.TimeManager2Team, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getTimeManagerTeamByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateTimeManagerTeam(timeManagerTeam: TimeManagerTeam, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateTimeManagerTeamCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateTimeManagerTeamUpdate(timeManagerTeam: TimeManagerTeam, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get Many

  async getAllTimeManagerTeamsByTeamId(teamId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerTeam[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<TimeManagerTeam>(StoreName.TimeManager2Team, GlobalConstants.KEY_NAMES.TeamId, teamId, TimeManagerTeam, transaction);

    let records = result as TimeManagerTeam[];

    if (!includeDeleted) {
      records = records.filter(timeManagerTeam => !this.checkIsTimeManagerTeamSoftDeleted(timeManagerTeam));
    }

    return records;
  }

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertTimeManagerTeamFromSync(who: DataChangeEmitWho, recordToUpsert: TimeManagerTeam, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getTimeManagerTeamByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateTimeManagerTeam(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createTimeManagerTeam(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsTimeManagerTeamSoftDeleted(record: TimeManagerTeam): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: TimeManagerTeam, b: TimeManagerTeam): boolean {
    return a.ManagerId === b.ManagerId && a.TeamId === b.TeamId;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
